/**
 * RetentionAttrition — Stage 3.
 * Live analytics from clinicians (start_date + termination_date) joined
 * with hr_separations. Historical exits without a separation record show
 * up as category "unknown" so the numbers work even before HR back-fills.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { fmtMonYY } from '../../lib/date';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Sparkles, UserMinus, Users2, TrendingDown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A', ok: '#3B6F5D',
};

function KpiCard({ label, value, sub, tone, testId, icon: Icon }) {
  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: tone || C.plum }}>
        {Icon && <Icon size={14} />} {label}
      </div>
      <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>{value}</div>
      {sub && <div className="mt-1 text-[11px]" style={{ color: C.soft }}>{sub}</div>}
    </Card>
  );
}

function months(days) {
  if (days == null) return '—';
  return `${(days / 30.4).toFixed(1)} mo`;
}

function SeparationDialog({ open, onClose, onSaved }) {
  const [clinicians, setClinicians] = useState([]);
  const [enums, setEnums] = useState({ voluntary: [], involuntary: [], planned_end: [] });
  const [form, setForm] = useState({
    clinician_id: '', last_day: '', notice_given_date: '',
    primary_reason: '', secondary_reason: '', regrettable: false,
    rehire_eligible: true, exit_interview_completed: false, note: '',
  });
  useEffect(() => {
    if (!open) return;
    api.get('/clinicians').then(({ data }) => setClinicians(data.clinicians || data || []));
    api.get('/hr/retention/enums').then(({ data }) => setEnums(data));
  }, [open]);
  const allReasons = [
    ...enums.voluntary.map(r => ({ r, cat: 'Voluntary' })),
    ...enums.involuntary.map(r => ({ r, cat: 'Involuntary' })),
    ...enums.planned_end.map(r => ({ r, cat: 'Planned end' })),
  ];
  const submit = async () => {
    if (!form.clinician_id || !form.last_day || !form.primary_reason) {
      return toast.error('Clinician, last day, and reason are required');
    }
    if (form.primary_reason.startsWith('Other') && !form.note.trim()) {
      return toast.error('Note required when reason is Other');
    }
    try {
      await api.post('/hr/separations', form);
      toast.success('Separation recorded');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="hr-sep-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Record separation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px]">Clinician</Label>
            <Select value={form.clinician_id} onValueChange={v => setForm({ ...form, clinician_id: v })}>
              <SelectTrigger data-testid="hr-sep-clinician"><SelectValue placeholder="Pick a clinician" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {clinicians.map(c => (
                  <SelectItem key={c.clinician_id} value={c.clinician_id}>
                    {c.first_name} {c.last_name} · {c.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Last day</Label>
              <Input type="date" value={form.last_day} onChange={e => setForm({ ...form, last_day: e.target.value })} data-testid="hr-sep-last-day" />
            </div>
            <div>
              <Label className="text-[11px]">Notice given</Label>
              <Input type="date" value={form.notice_given_date} onChange={e => setForm({ ...form, notice_given_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Primary reason</Label>
            <Select value={form.primary_reason} onValueChange={v => setForm({ ...form, primary_reason: v })}>
              <SelectTrigger data-testid="hr-sep-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {allReasons.map(({ r, cat }) => (
                  <SelectItem key={r} value={r}>{cat}: {r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.primary_reason.startsWith('Other') && (
            <div>
              <Label className="text-[11px]">Note (required for Other)</Label>
              <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="hr-sep-note" />
            </div>
          )}
          <div className="flex items-center gap-6 flex-wrap text-[12px]">
            <label className="flex items-center gap-2">
              <Switch checked={form.regrettable} onCheckedChange={v => setForm({ ...form, regrettable: v })} data-testid="hr-sep-regrettable" />
              Regrettable
            </label>
            <label className="flex items-center gap-2">
              <Switch checked={form.rehire_eligible} onCheckedChange={v => setForm({ ...form, rehire_eligible: v })} data-testid="hr-sep-rehire" />
              Rehire eligible
            </label>
            <label className="flex items-center gap-2">
              <Switch checked={form.exit_interview_completed} onCheckedChange={v => setForm({ ...form, exit_interview_completed: v })} />
              Exit interview done
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="hr-sep-submit">Save separation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RetentionAttrition() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includePlanned, setIncludePlanned] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/hr/retention', { params: { include_planned: includePlanned } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load retention analytics'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includePlanned]);

  const tenureChart = useMemo(
    () => (data?.tenure?.bands || []).map(b => ({ band: b.band, count: b.count })),
  [data]);

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: C.soft }}>Loading retention & attrition…</div>;
  if (!data) return null;

  const t = data.tenure || {};
  const a = data.attrition || {};

  return (
    <div className="space-y-4" data-testid="hr-retention-attrition">
      <div className="flex items-center gap-2">
        <label className="text-[11px] flex items-center gap-2" style={{ color: C.ink2 }}>
          <Switch checked={includePlanned} onCheckedChange={setIncludePlanned} data-testid="hr-retention-toggle-planned" />
          Include planned ends (externs, PRN closes)
        </label>
        <div className="ml-auto">
          <Button size="sm" onClick={() => setDlgOpen(true)} data-testid="hr-retention-record-sep">
            <UserMinus size={14} className="mr-1" /> Record separation
          </Button>
        </div>
      </div>

      {/* 4 KPI cards */}
      <div className="grid md:grid-cols-4 gap-3">
        <KpiCard testId="hr-ret-kpi-attr-365" icon={TrendingDown} label="Attrition · last 365 days"
                 value={a.d365?.total ?? 0}
                 sub={`${a.d365?.rate_pct ?? '—'}% · ${a.d365?.voluntary || 0} vol · ${a.d365?.involuntary || 0} invol · ${a.d365?.regrettable || 0} regrettable`}
                 tone={C.rose} />
        <KpiCard testId="hr-ret-kpi-attr-90" icon={UserMinus} label="Attrition · last 90 days"
                 value={a.d90?.total ?? 0}
                 sub={`${a.d90?.rate_pct ?? '—'}% · ${a.d90?.voluntary || 0} vol · ${a.d90?.involuntary || 0} invol`}
                 tone={C.gold} />
        <KpiCard testId="hr-ret-kpi-early-90" icon={Clock} label="Early attrition · 90-day"
                 value={data.early_attrition?.d90?.rate_pct == null ? '—' : `${data.early_attrition.d90.rate_pct}%`}
                 sub={`${data.early_attrition?.d90?.left || 0} of ${data.early_attrition?.d90?.cohort || 0} recent hires`}
                 tone={C.rose} />
        <KpiCard testId="hr-ret-kpi-tenure" icon={Users2} label="Median tenure (active)"
                 value={months(t.median_days)}
                 sub={`Mean ${months(t.mean_days)} · median at exit ${months(t.median_days_at_exit)}`}
                 tone={C.plum} />
      </div>

      {/* Insights */}
      {data.insights?.length > 0 && (
        <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-ret-insights">
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border, background: '#F4F1F8' }}>
            <Sparkles size={16} style={{ color: C.plum }} />
            <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: C.plum }}>Patterns worth investigating</div>
          </div>
          <div className="divide-y" style={{ borderColor: C.border }}>
            {data.insights.map((i, idx) => (
              <div key={idx} className="px-4 py-3 text-sm" style={{ color: C.ink }} data-testid={`hr-ret-insight-${idx}`}>
                {i.label}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Retention by month */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-ret-retention-series">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Monthly retention rate · last 12 months</div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.retention_series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="month" fontSize={10} tickLine={false} tickFormatter={fmtMonYY} />
              <YAxis fontSize={10} tickLine={false} domain={[80, 100]} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => v == null ? '—' : `${v}%`} />
              <Line type="monotone" dataKey="retention_pct" name="Retention"
                    stroke={C.ok} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Tenure distribution */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-ret-tenure-bands">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Tenure distribution · active staff</div>
        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={tenureChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="band" fontSize={11} tickLine={false} />
              <YAxis fontSize={10} tickLine={false} />
              <Tooltip />
              <Bar dataKey="count" fill={C.plum} radius={[4, 4, 0, 0]}>
                {tenureChart.map((_, i) => <Cell key={i} fill={C.plum} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Attrition by clinic + role */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-ret-by-clinic">
          <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
               style={{ borderColor: C.border, background: C.paper, color: C.ink2 }}>
            Attrition by clinic · last 12 months
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(data.by_clinic || []).length === 0 && (
                <tr><td className="px-4 py-4 text-center text-sm" style={{ color: C.soft }}>No exits recorded</td></tr>
              )}
              {(data.by_clinic || []).map((row, i) => (
                <tr key={i} className="border-t" style={{ borderColor: C.border }}>
                  <td className="px-4 py-2" style={{ color: C.ink }}>{row.clinic}</td>
                  <td className="px-4 py-2 text-right font-serif" style={{ color: C.ink }}>{row.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-ret-by-role">
          <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
               style={{ borderColor: C.border, background: C.paper, color: C.ink2 }}>
            Attrition by role · last 12 months
          </div>
          <table className="w-full text-sm">
            <tbody>
              {(data.by_role || []).length === 0 && (
                <tr><td className="px-4 py-4 text-center text-sm" style={{ color: C.soft }}>No exits recorded</td></tr>
              )}
              {(data.by_role || []).map((row, i) => (
                <tr key={i} className="border-t" style={{ borderColor: C.border }}>
                  <td className="px-4 py-2" style={{ color: C.ink }}>{row.role}</td>
                  <td className="px-4 py-2 text-right font-serif" style={{ color: C.ink }}>{row.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Exit reasons */}
      <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-ret-reasons">
        <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
             style={{ borderColor: C.border, background: C.paper, color: C.ink2 }}>
          Exit reasons · last 12 months
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-center px-2 py-2">Voluntary</th>
              <th className="text-center px-2 py-2">Involuntary</th>
              <th className="text-center px-2 py-2">Planned</th>
              <th className="text-center px-2 py-2">Unknown</th>
              <th className="text-right px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.exit_reasons || []).length === 0 && (
              <tr><td colSpan="6" className="px-4 py-4 text-center text-sm" style={{ color: C.soft }}>No exits recorded. Use "Record separation" to log one.</td></tr>
            )}
            {(data.exit_reasons || []).map((row, i) => (
              <tr key={i} className="border-t" style={{ borderColor: C.border }}>
                <td className="px-4 py-2" style={{ color: C.ink }}>{row.reason}</td>
                <td className="px-2 py-2 text-center" style={{ color: C.rose }}>{row.voluntary || '—'}</td>
                <td className="px-2 py-2 text-center" style={{ color: '#B54B4B' }}>{row.involuntary || '—'}</td>
                <td className="px-2 py-2 text-center" style={{ color: C.ink2 }}>{row.planned_end || '—'}</td>
                <td className="px-2 py-2 text-center" style={{ color: C.soft }}>{row.unknown || '—'}</td>
                <td className="px-4 py-2 text-right font-serif" style={{ color: C.ink }}>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <SeparationDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onSaved={load} />
    </div>
  );
}
